import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { useNombreDePaniers } from '../utils/panier';
import { degradeVertical } from '../theme/jetons';
import MarqueOptimStock from './commun/MarqueOptimStock';

const drawerWidth = 216; // 240 * 0.9

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'disableScrollLock',
})(({ theme, open }) => ({
  width: open ? drawerWidth : 0, // Le conteneur prend 0px quand fermé
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    boxSizing: 'border-box',
    background: degradeVertical(theme.palette.background.default, theme.palette.divider),
    // ⚠️ MÊME FAMILLE QUE L'AVATAR, TROUVÉE EN LE CORRIGEANT. Une bordure
    // NOIRE à 8 % sépare deux surfaces claires ; sur fond sombre elle ne se
    // voit pas. Ce qui sépare deux surfaces sombres est une ligne CLAIRE —
    // c'est exactement ce que `divider` sait, et pas un littéral.
    borderRight: open ? `1px solid ${theme.palette.divider}` : 'none',
    boxShadow: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const getMenuItems = (t) => [
  {
    text: t('navigation.dashboard'),
    icon: <DashboardIcon />,
    path: '/',
  },
  {
    text: t('navigation.products'),
    icon: <InventoryIcon />,
    path: '/products',
  },
  {
    text: t('navigation.suppliers'),
    icon: <BusinessIcon />,
    path: '/suppliers',
  },
  {
    text: t('navigation.orders'),
    icon: <ShoppingCartIcon />,
    path: '/purchase-orders',
    // 🔴 LE BADGE COMPTE LES PANIERS, PAS LES LIGNES. « 2 » veut dire deux
    // commandes en attente, ce qui est actionnable ; « 7 lignes réparties on ne
    // sait comment » ne l'est pas. Et il est PAR STOCK — un compteur qui
    // désigne ce qu'on ne peut pas atteindre sans changer de périmètre est pire
    // qu'un compteur absent.
    badge: 'paniers',
  },
  {
    text: t('navigation.reception'),
    icon: <LocalShippingIcon />,
    path: '/order-reception',
  },
  {
    text: t('navigation.accounting'),
    icon: <PaymentIcon />,
    path: '/accounting',
  },
  {
    text: t('navigation.scan'),
    icon: <QrCodeScannerIcon />,
    path: '/scan',
  },
];

const Sidebar = ({ 
  open, 
  onClose, 
  unreadCount = 0, 
  onToggle,
  currentStock,
  onStockChange,
  stocks = [],
  user,
  onLogout
}) => {
  // Le compteur de paniers vit ICI plutôt qu'en prop : il dépend du stock
  // courant, que la barre latérale connaît déjà, et le faire descendre depuis
  // `App` obligerait chaque écran qui touche au panier à le remonter.
  const nombreDePaniers = useNombreDePaniers(currentStock?.id);

  const navigate = useNavigate();
  const { t } = useLanguage();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    // Fermer la sidebar sur mobile
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <StyledDrawer variant="permanent" open={open} disableScrollLock>
      <Box 
        sx={{ 
          overflow: 'auto',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* En-tête du drawer avec logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            minHeight: 64,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
            {/* Le bloc d'identité — écrit une fois, pour la Sidebar et les deux
                portes. `degradeTexte` n'est passé QUE d'ici : c'est la seule
                surface où le nom est le seul titre de la colonne. */}
            <MarqueOptimStock degradeTexte />
        </Box>

        <Divider />

        {/* Sélecteur de stock */}
        {stocks.length > 0 && (
          <Box sx={{ p: open ? 2 : 1, borderBottom: 1, borderColor: 'divider' }}>
            {open ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {t('dashboard.activeStock')}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={currentStock?.id || ''}
                    onChange={(e) => {
                      const selectedStock = stocks.find(s => s.id === e.target.value);
                      onStockChange(selectedStock);
                    }}
                    displayEmpty
                  >
                    {stocks.map((stock) => (
                      <MenuItem key={stock.id} value={stock.id}>
                        {stock.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label={currentStock?.name?.substring(0, 2).toUpperCase() || 'ST'}
                  color="primary"
                  size="small"
                  sx={{ 
                    minWidth: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Menu de navigation */}
        <List>
          {getMenuItems(t).map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor: isActive ? 'action.selected' : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive ? 'action.selected' : 'action.hover',
                    },
                  }}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: isActive ? 'primary.main' : 'inherit',
                    }}
                  >
                    {item.badge ? (
                      <Badge
                        badgeContent={item.badge === 'paniers' ? nombreDePaniers : unreadCount}
                        color={item.badge === 'paniers' ? 'primary' : 'error'}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: open ? 1 : 0,
                      color: isActive ? 'primary.main' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider />

        {/* Menu Administration (admin seulement) */}
        {user && user.role === 'ADMIN' && (
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
                onClick={() => handleNavigation('/admin')}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('navigation.admin')}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}

        <Divider />

        {/* Bouton de déconnexion */}
        {user && (
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
                onClick={onLogout}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('navigation.logout')}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar; 